"use client";

/**
 * App-registered "add to chat context" button for `ModelDetailView`.
 *
 * Mirrors `FieldOverridesStore`: the app registers a single button
 * component once at boot (on the framework's Jotai store), and every
 * `ModelDetailView` — anywhere in the route tree, no Provider needed —
 * renders it in its header via `useAddToContextButton()`.
 *
 * Layering: the actual "add to context" behaviour (the `useAddToContext`
 * hook, chat atoms, router) lives in the app and must not be imported by
 * the framework package. So the app injects a component here instead; the
 * framework just renders whatever was registered, passing the loaded node.
 *
 * App side (called once at import — see `config/add-to-context-button`):
 *
 *     import { setAddToContextButton } from "@sourcepilot/ui/framework/ui/views";
 *     setAddToContextButton(AddToContextButton);
 */

import React from "react";
import { atom, useAtomValue } from "jotai";
import { graphStoreJotaiStore } from "@sourcepilot/framework/graph-store";

/**
 * Button component rendered in the `ModelDetailView` header. Receives the
 * loaded node (id / name / search_path / _label) and its model name so it
 * can build the context item.
 */
export type AddToContextButton = React.ComponentType<{
  node: any;
  /** Model name — supplied by `ModelDetailView`; optional so other call
   *  sites (e.g. `InPlaceEditable`) can render the button with just a node. */
  model?: string;
  /** Wrapper className — lets call sites match local styling (e.g.
   *  `InPlaceEditable`'s hover-reveal). */
  className?: string;
}>;

const addToContextButtonAtom = atom<AddToContextButton | null>(null);

/**
 * Register the app's "add to context" button. Call once at app boot;
 * calling again replaces it. Pass `null` to clear. Lives on the
 * graph-store's Jotai store so it's a global one-shot, not Provider-scoped.
 */
export function setAddToContextButton(
  component: AddToContextButton | null,
): void {
  // A component is a function; Jotai's plain `set` would treat it as an
  // updater. Wrap it so the stored value is the component itself.
  graphStoreJotaiStore.set(addToContextButtonAtom, () => component);
}

/**
 * The registered button, or `null` when the app hasn't registered one
 * (the common case — most apps have no chat-context feature).
 */
export function useAddToContextButton(): AddToContextButton | null {
  return useAtomValue(addToContextButtonAtom, { store: graphStoreJotaiStore });
}
